import React, { useRef, useState } from 'react';
import { ai_services } from '../../../../../services/request/ai';
import { FormControl, MenuItem, Select, TextField, IconButton, Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import Modal from '../../../../../components/Modal';
import { CloseIcon_SVG } from '../../../../../utils/defaults';
import { useSelector } from 'react-redux';

// --- DATA ---
const paletteLibrary = [
	{
		name: 'Vibrant Contrasts',
		variations: [
			['#000080', '#9400D3'],
			['#4B0082', '#FF1493'],
			['#000080', '#FF69B4'],
			['#003366', '#66FF66'],
		],
	},
	{
		name: 'Earthy Tones',
		variations: [
			['#A0522D', '#D2B48C'],
			['#8B4513', '#CD853F'],
			['#000000', '#F5DEB3'],
			['#2E8B57', '#DAA520'],
		],
	},
	{
		name: 'Cool Blues',
		variations: [
			['#ADD8E6', '#4682B4'],
			['#00BFFF', '#FFA500'],
			['#6495ED', '#4169E1'],
			['#0000CD', '#00FFFF'],
		],
	},
	{
		name: 'Warm Sunsets',
		variations: [
			['#FF7F50', '#FF6347'],
			['#FFD700', '#FFA500'],
			['#FF4500', '#B22222'],
			['#E9967A', '#000080'],
		],
	},
	{
		name: 'Soft Pastels',
		variations: [
			['#FFB6C1', '#800080'],
			['#D8BFD8', '#FFC0CB'],
			['#FAEBD7', '#FF7F50'],
			['#F0F8FF', '#FF6347'],
		],
	},
	{
		name: 'Monochromatic Grays',
		variations: [
			['#FF0000', '#D3D3D3'],
			['#000000', '#FF0000'],
			['#0000FF', '#D3D3D3'],
			['#FF0000', '#D3D3D3'],
		],
	},
	{
		name: 'Nature Inspired',
		variations: [
			['#006400', '#228B22'],
			['#006400', '#2E8B57'],
			['#228B22', '#32CD32'],
			['#006400', '#00FF00'],
		],
	},
	{
		name: 'Elegant Darks',
		variations: [
			['#000000', '#2E8B57'],
			['#000000', '#0000FF'],
			['#000000', '#4B0082'],
			['#000000', '#FFD700'],
		],
	},
	{
		name: 'Bright And Cheerful',
		variations: [
			['#FFFF00', '#FFA500'],
			['#8B4513', '#FFD700'],
			['#000000', '#FF0000'],
			['#FF69B4', '#FF1493'],
		],
	},
	{
		name: 'Subtle And Professional',
		variations: [
			['#2E8B57', '#F5F5F5'],
			['#006400', '#FF0000'],
			['#4682B4', '#F5F5F5'],
			['#2E8B57', '#F5F5F5'],
		],
	},
	{
		name: 'Bold Accents',
		variations: [
			['#000000', '#0000FF'],
			['#000000', '#FFFF00'],
			['#000000', '#800080'],
			['#800080', '#FF1493'],
		],
	},
	{
		name: 'Gentle Neutrals',
		variations: [
			['#FFC0CB', '#D2B48C'],
			['#FFD700', '#8B4513'],
			['#F5DEB3', '#A0522D'],
			['#D2B48C', '#8B4513'],
		],
	},
	{
		name: 'Calming Blues',
		variations: [
			['#ADD8E6', '#00FFFF'],
			['#00FFFF', '#008B8B'],
			['#00FFFF', '#00CED1'],
			['#00BFFF', '#FF6347'],
		],
	},
	{
		name: 'Vibrant Pinks And Purples',
		variations: [
			['#DDA0DD', '#800080'],
			['#800080', '#8B008B'],
			['#FF1493', '#C71585'],
			['#FF69B4', '#FF0000'],
		],
	},
	{
		name: 'Muted Earth Tones',
		variations: [
			['#D2B48C', '#008080'],
			['#A0522D', '#00FFFF'],
			['#8B4513', '#00008B'],
			['#D2B48C', '#FFD700'],
		],
	},
	{
		name: 'High Contrast Light',
		variations: [
			['#0000FF', '#FFFF00'],
			['#000080', '#FFD700'],
			['#FFFFFF', '#FFD700'],
			['#0000FF', '#800080'],
		],
	},
	{
		name: 'Soft And Soothing',
		variations: [
			['#FF0000', '#DDA0DD'],
			['#800080', '#FFB6C1'],
			['#FFD700', '#FF8C00'],
			['#FF69B4', '#FF6347'],
		],
	},
	{
		name: 'Bold And Unique',
		variations: [
			['#000000', '#FF6347'],
			['#000080', '#FFA500'],
			['#0000FF', '#008080'],
			['#006400', '#00FFFF'],
		],
	},
];

// --- INTERFACES ---
interface ColorPaletteProps {
	onClose: () => void;
	onPaletteSelect: (data: string[] | string | any) => void;
	activeCategory?: { name: string };
	categoriesList?: Array<{ name: string; _id: string }>;
}

// --- SUB-COMPONENTS ---
const XMarkIcon = () => (
	<svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
		<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
	</svg>
);

// Loading spinner (white color for visibility on colored buttons)
const LoadingSpinner = () => (
	<svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
		<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
		<path
			className="opacity-75"
			fill="currentColor"
			d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
		></path>
	</svg>
);

const PaletteVariation = ({
	colors,
	onClick,
	isSelected,
}: {
	colors: string[];
	onClick: () => void;
	isSelected: boolean;
}) => (
	<button
		onClick={onClick}
		className={`w-12 h-8 rounded border-2 transition-all duration-200 ${
			isSelected ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-gray-300 hover:border-gray-400'
		}`}
	>
		<div className="flex h-full rounded overflow-hidden">
			{colors.map((color, index) => (
				<div key={index} style={{ backgroundColor: color }} className="h-full w-full" />
			))}
		</div>
	</button>
);

const PaletteCategory = ({
	name,
	variations,
	onSelect,
	selectedColors,
}: {
	name: string;
	variations: string[][];
	onSelect: (colors: string[]) => void;
	selectedColors: string[] | null;
}) => (
	<div className="mb-3">
		<h3 className="text-xs font-medium text-gray-700 mb-1.5">{name}</h3>
		<div className="grid grid-cols-4 gap-1.5">
			{variations.map((colors, index) => (
				<PaletteVariation
					key={index}
					colors={colors}
					onClick={() => onSelect(colors)}
					isSelected={selectedColors !== null && JSON.stringify(selectedColors) === JSON.stringify(colors)}
				/>
			))}
		</div>
	</div>
);

const AIInputForm = ({
	selectedColors,
	activeCategory,
	onBack,
	onGenerateImage,
	onClose,
	categoriesList,
	isGenerating,
}: {
	selectedColors: string[];
	activeCategory?: { name: string };
	onBack: () => void;
	onGenerateImage: (data: any) => void;
	onClose: () => void;
	categoriesList?: Array<{ name: string; _id: string }>;
	isGenerating: boolean;
}) => {
	const initialOccasion =
		activeCategory?.name && activeCategory.name !== 'AI cover generator' ? activeCategory.name : '';
	const [occasion, setOccasion] = useState(initialOccasion);
	const [customMessage, setCustomMessage] = useState('');
	const [fontStyle, setFontStyle] = useState('Professional & Corporate');
	const [layoutStyle, setLayoutStyle] = useState('Centered & Balanced');
	const [aspectRatio, setAspectRatio] = useState('1:1');
	const [isCustomCategory, setIsCustomCategory] = useState(false);
	const [customOccasion, setCustomOccasion] = useState('');
	// No complex state; use MUI Tooltip for robust hover UX

	const occasionStyleOptions = [
		{
			value: 'Professional & Corporate',
			label: 'Professional & Corporate',
			description:
				'Clean, minimalist design with serif fonts. Perfect for business, employee recognition, and formal occasions.',
			bestFor: ['Employee Recognition', 'Work Anniversary', 'Boss day', 'Promotion', 'Milestone'],
		},
		{
			value: 'Elegant & Sophisticated',
			label: 'Elegant & Sophisticated',
			description: 'Refined typography with decorative elements. Ideal for special celebrations and formal events.',
			bestFor: ['Wedding', 'Anniversary Cards', 'Congratulation Cards', 'Graduation'],
		},
		{
			value: 'Warm & Heartfelt',
			label: 'Warm & Heartfelt',
			description: 'Soft, flowing fonts with emotional appeal. Great for personal messages and caring sentiments.',
			bestFor: ['Thank You Card', 'Get Well Soon', 'Sympathy', "Mother's day", "Father's day"],
		},
		{
			value: 'Fun & Playful',
			label: 'Fun & Playful',
			description: 'Vibrant, energetic fonts with dynamic layouts. Perfect for celebrations and festive occasions.',
			bestFor: ['Birthday Cards', 'Halloween Eve', 'Party callouts', 'Summer day', 'Best Friends'],
		},
		{
			value: 'Traditional & Classic',
			label: 'Traditional & Classic',
			description: 'Timeless fonts with traditional layouts. Suitable for holidays and cultural celebrations.',
			bestFor: ['Christmas', 'Easter Eve', 'Happy Thanksgiving', 'Hanukkah', 'New Year 2023'],
		},
		{
			value: 'Modern & Minimalist',
			label: 'Modern & Minimalist',
			description: 'Clean, contemporary sans-serif fonts. Works well for tech-savvy and younger audiences.',
			bestFor: ['Product Feature Updates', 'User Onboarding', 'Webinar Reminder', 'Trial'],
		},
		{
			value: 'Handwritten & Personal',
			label: 'Handwritten & Personal',
			description: 'Script and handwritten-style fonts. Adds a personal, intimate touch to any message.',
			bestFor: ["Valentine's Day", 'Love cards', 'Best Friends', 'Thank You Card'],
		},
		{
			value: 'Bold & Impactful',
			label: 'Bold & Impactful',
			description: 'Strong, attention-grabbing fonts. Great for announcements and important messages.',
			bestFor: ['Sales Reports', 'Milestone', 'Football', 'Volleyball', 'Employee Motivation'],
		},
		{
			value: 'Artistic & Creative',
			label: 'Artistic & Creative',
			description: 'Unique, decorative fonts with creative flair. Perfect for special themes and unique occasions.',
			bestFor: ['Spring', "International Women's Day", 'Pride / LGBT', 'St patricks day'],
		},
		{
			value: 'Friendly & Approachable',
			label: 'Friendly & Approachable',
			description: 'Rounded, welcoming fonts. Ideal for customer communications and team building.',
			bestFor: ['Employee Welcome', 'Customer Onboarding', 'Client Appreciation', 'Meeting Reminders'],
		},
	];

	// Enhanced Layout Options with better descriptions
	const layoutOptions = [
		{
			value: 'Centered & Balanced',
			label: 'Centered & Balanced',
			description: 'Classic centered layout with symmetrical design',
		},
		{
			value: 'Dynamic Diagonal',
			label: 'Dynamic Diagonal',
			description: 'Modern angled text placement for energy and movement',
		},
		{
			value: 'Corner Accent',
			label: 'Corner Accent',
			description: 'Text positioned in corners with decorative elements',
		},
		{
			value: 'Banner Style',
			label: 'Banner Style',
			description: 'Text in ribbon or banner format across the card',
		},
		{
			value: 'Split Layout',
			label: 'Split Layout',
			description: 'Divided sections for multiple messages or elements',
		},
		{
			value: 'Frame Border',
			label: 'Frame Border',
			description: 'Text framed with decorative borders',
		},
		{
			value: 'Artistic Scatter',
			label: 'Artistic Scatter',
			description: 'Creative text placement with varied sizes and positions',
		},
		{
			value: 'Minimalist Float',
			label: 'Minimalist Float',
			description: 'Simple text floating with plenty of white space',
		},
	];

	const aspectRatioOptions = [
		{
			value: '1:1',
			label: 'Square (1:1)',
			description: 'Perfect for social media posts',
		},
		{
			value: '4:3',
			label: 'Standard (4:3)',
			description: 'Classic greeting card format',
		},
		{
			value: '3:2',
			label: 'Photo (3:2)',
			description: 'Traditional photo proportions',
		},
		{
			value: '16:9',
			label: 'Wide (16:9)',
			description: 'Great for digital displays and emails',
		},
		{
			value: '9:16',
			label: 'Story (9:16)',
			description: 'Ideal for mobile stories and vertical displays',
		},
		{
			value: '5:7',
			label: 'Portrait (5:7)',
			description: 'Traditional portrait card format',
		},
	];

	const handleGenerate = async () => {
		const finalOccasion = (isCustomCategory ? customOccasion : occasion)?.trim();
		if (!finalOccasion) {
			toast.error('Please select or enter an occasion', {
				position: 'top-right',
				autoClose: 3000,
				pauseOnFocusLoss: false,
			});
			return;
		}

		// Map selected values to their descriptive metadata (with safe fallbacks)
		const selectedOccasionStyle = occasionStyleOptions.find((o) => o.value === fontStyle) || occasionStyleOptions[0];
		const selectedLayoutStyle = layoutOptions.find((o) => o.value === layoutStyle) || layoutOptions[0];
		const selectedAspectRatio = aspectRatioOptions.find((o) => o.value === aspectRatio) || aspectRatioOptions[0];

		const formData = {
			occasion: finalOccasion,
			customMessage,
			colorPalette: selectedColors,
			fontStyle: selectedOccasionStyle?.value,
			layoutStyle: selectedLayoutStyle?.value,
			aspectRatio: selectedAspectRatio?.value,
			// Provide descriptions to help LLM understand intent
			fontStyleDescription: selectedOccasionStyle?.description,
			layoutStyleDescription: selectedLayoutStyle?.description,
			aspectRatioDescription: selectedAspectRatio?.description,
		};

		await onGenerateImage(formData);
	};

	return (
		<div className="space-y-3">
			{/* Header with selected colors */}
			<div className="border-b border-gray-200 pb-2">
				<div className="flex items-center justify-between mb-2">
					<h2 className="text-sm font-medium text-gray-800">AI Cover Generator</h2>
					<div className="flex items-center gap-1">
						<Tooltip
							title={
								<div className="text-left">
									<div className="text-sm font-semibold text-gray-800 mb-1">Tips for Better Results</div>
									<ul className="space-y-1.5 text-[12px] leading-snug">
										<li className="flex items-start gap-1.5">
											<span className="mt-[2px] text-green-600">✓</span>
											<span>Describe visual elements (colors, objects, style)</span>
										</li>
										<li className="flex items-start gap-1.5">
											<span className="mt-[2px] text-green-600">✓</span>
											<span>Specify themes or occasions (birthday, farewell, etc.)</span>
										</li>
										<li className="flex items-start gap-1.5">
											<span className="mt-[2px] text-green-600">✓</span>
											<span>Include specific details (setting, mood, time of day)</span>
										</li>
										<li className="flex items-start gap-1.5">
											<span className="mt-[2px] text-red-500">✗</span>
											<span>Avoid requesting text in the image</span>
										</li>
										<li className="flex items-start gap-1.5">
											<span className="mt-[2px] text-red-500">✗</span>
											<span>Avoid vague descriptions</span>
										</li>
									</ul>
								</div>
							}
							arrow
							placement="bottom-end"
							enterDelay={120}
							leaveDelay={120}
							slotProps={{
								tooltip: {
									sx: {
										backgroundColor: '#ecfdf5',
										color: '#111827',
										border: '1px solid #34d399',
										p: 1.5,
										maxWidth: 280,
										boxShadow: 3,
									},
								},
								arrow: {
									sx: {
										color: '#ecfdf5',
										'&:before': { border: '1px solid #34d399', backgroundColor: '#ecfdf5' },
									},
								},
							}}
						>
							<IconButton size="small" aria-label="Tips for better results">
								<span
									className="flex items-center justify-center h-5 w-5 rounded-full border border-green-500 text-green-600 text-[11px]"
									style={{ lineHeight: 1 }}
								>
									?
								</span>
							</IconButton>
						</Tooltip>
						<button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
							<XMarkIcon />
						</button>
					</div>
				</div>
				<div className="flex items-center space-x-2">
					<span className="text-xs text-gray-600">Selected Colors:</span>
					<div className="flex h-4 w-10 rounded overflow-hidden border border-gray-300">
						{selectedColors.map((color, index) => (
							<div key={index} style={{ backgroundColor: color }} className="h-full w-full" />
						))}
					</div>
				</div>
			</div>

			{/* Tooltip handles hover/focus lifecycle; no manual Popper needed */}

			{/* Form Fields */}
			<div className="space-y-2.5">
				{/* Occasion */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Occasion *</label>
					<FormControl fullWidth size="small" disabled={isGenerating} sx={{ '& .MuiInputBase-root': { height: 34 } }}>
						<Select
							displayEmpty
							value={isCustomCategory ? '__custom__' : occasion || ''}
							onChange={(e) => {
								const val = String(e.target.value || '');
								if (val === '__custom__') {
									setIsCustomCategory(true);
									setOccasion('');
								} else {
									setIsCustomCategory(false);
									setOccasion(val);
								}
							}}
							renderValue={(selected) => {
								if (selected === '' || selected === undefined) return 'Select occasion...';
								if (selected === '__custom__') return 'Custom category...';
								return String(selected);
							}}
							sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: 14 } }}
							MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
						>
							<MenuItem value="" sx={{ fontSize: 14, py: 0.5 }}>
								<em>Select occasion...</em>
							</MenuItem>
							{(categoriesList || [])
								.filter((c) => c?.name !== 'AI cover generator')
								.map((category) => (
									<MenuItem key={category._id} value={category.name} sx={{ fontSize: 12, py: 0.5 }}>
										{category.name}
									</MenuItem>
								))}
							<MenuItem value="__custom__" sx={{ fontSize: 14, py: 0.5 }}>
								Custom category...
							</MenuItem>
						</Select>
					</FormControl>
					{isCustomCategory && (
						<div className="mt-4">
							<label className="block text-xs font-medium text-gray-700 ">Category Name *</label>
							<TextField
								required
								fullWidth
								size="small"
								margin="dense"
								variant="outlined"
								value={customOccasion}
								onChange={(e) => setCustomOccasion(e.target.value)}
								placeholder="e.g., Team Celebration"
								InputLabelProps={{ shrink: true }}
								sx={{
									'& .MuiInputBase-input': { fontSize: 14, py: '6px' },
								}}
							/>
						</div>
					)}
				</div>

				{/* Custom Message */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Custom Message</label>
					<textarea
						value={customMessage}
						onChange={(e) => setCustomMessage(e.target.value)}
						placeholder="Enter your custom message..."
						className="w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400"
						rows={2}
						disabled={isGenerating}
					/>
				</div>

				{/* Occasion Style */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Occasion Style</label>
					<FormControl fullWidth size="small" disabled={isGenerating} sx={{ '& .MuiInputBase-root': { height: 34 } }}>
						<Select
							value={fontStyle}
							onChange={(e) => setFontStyle(String(e.target.value))}
							sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: 12 } }}
							MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
						>
							{occasionStyleOptions.map((opt) => (
								<MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12, py: 0.5 }}>
									{opt.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</div>

				{/* Layout Style */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Layout Style</label>
					<FormControl fullWidth size="small" disabled={isGenerating} sx={{ '& .MuiInputBase-root': { height: 34 } }}>
						<Select
							value={layoutStyle}
							onChange={(e) => setLayoutStyle(String(e.target.value))}
							sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: 12 } }}
							MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
						>
							{layoutOptions.map((opt) => (
								<MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12, py: 0.5 }}>
									{opt.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</div>

				{/* Aspect Ratio */}
				<div>
					<label className="block text-xs font-medium text-gray-700 mb-1">Aspect Ratio</label>
					<FormControl fullWidth size="small" disabled={isGenerating} sx={{ '& .MuiInputBase-root': { height: 34 } }}>
						<Select
							value={aspectRatio}
							onChange={(e) => setAspectRatio(String(e.target.value))}
							sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: 12 } }}
							MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
						>
							{aspectRatioOptions.map((opt) => (
								<MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12, py: 0.5 }}>
									{opt.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</div>
			</div>

			{/* Action Buttons */}
			<div className="flex space-x-2 pt-2 border-t border-gray-200 mt-2">
				<button
					onClick={onBack}
					disabled={isGenerating}
					className="flex-1 px-2 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
				>
					Back
				</button>
				<button
					onClick={handleGenerate}
					disabled={isGenerating}
					className="flex-1 px-2 py-1.5 text-xs font-medium bg-lightPurpleTheme hover:bg-addindigo text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
				>
					{isGenerating ? (
						<>
							<LoadingSpinner />
							Generating...
						</>
					) : (
						'Generate Cover'
					)}
				</button>
			</div>
		</div>
	);
};

// --- IMAGE MODAL COMPONENT ---
const ImagePreviewModal = ({
	open,
	onClose,
	generatedImages,
	currentImageIndex,
	selectedColors,
	onUseImage,
	onGenerateNew,
	onNavigateImage,
}: {
	open: boolean;
	onClose: () => void;
	generatedImages: Array<{ url: string; mimeType?: string; timestamp: number }>;
	currentImageIndex: number;
	selectedColors: string[];
	onUseImage: (imageUrl: string) => void;
	onGenerateNew: () => void;
	onNavigateImage: (index: number) => void;
}) => {
	const currentImage = generatedImages[currentImageIndex];
	const imageUrl = currentImage ? currentImage.url : '';

	return (
		<Modal
			open={open}
			onClose={onClose}
			closeIcon={CloseIcon_SVG}
			className="max-w-2xl w-full max-h-[90vh] m-4 z-50 relative flex flex-col bg-white rounded-lg shadow-xl"
			iconClassName="w-6 h-6 bg-white"
		>
			<div className="p-4 flex flex-col h-full overflow-hidden">
				{/* Header */}
				<div className="border-b border-gray-200 pb-3 mb-3">
					<div className="flex items-center justify-between">
						<h2 className="text-lg font-semibold text-gray-800">Generated Covers</h2>
						{generatedImages.length > 1 && (
							<span className="text-sm text-gray-600">
								{currentImageIndex + 1} of {generatedImages.length}
							</span>
						)}
					</div>
					<div className="flex items-center space-x-2 mt-2">
						<span className="text-sm text-gray-600">Color Palette:</span>
						<div className="flex h-5 w-14 rounded overflow-hidden border border-gray-300">
							{selectedColors.map((color, index) => (
								<div key={index} style={{ backgroundColor: color }} className="h-full w-full" />
							))}
						</div>
					</div>
				</div>

				{/* Image Gallery Navigation */}
				{generatedImages.length > 1 && (
					<div className="mb-3">
						<div className="flex space-x-2 overflow-x-auto pb-2">
							{generatedImages.map((image, index) => {
								const thumbUrl = image.url;
								return (
									<button
										key={index}
										onClick={() => onNavigateImage(index)}
										className={`flex-shrink-0 w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${
											index === currentImageIndex
												? 'border-blue-500 ring-2 ring-blue-200'
												: 'border-gray-300 hover:border-gray-400'
										}`}
									>
										<img src={thumbUrl} alt={`Generated cover ${index + 1}`} className="w-full h-full object-cover" />
									</button>
								);
							})}
						</div>
					</div>
				)}

				{/* Main Image Display */}
				<div className="flex-1 flex items-center justify-center bg-gray-50 rounded-lg p-3 min-h-0">
					<div className="max-w-xs max-h-80 bg-white rounded-lg shadow-md overflow-hidden">
						{currentImage && (
							<img
								src={imageUrl}
								alt="Generated cover"
								className="w-full h-full object-contain"
								style={{ maxHeight: '300px', maxWidth: '300px' }}
							/>
						)}
					</div>
				</div>

				{/* Navigation Arrows */}
				{generatedImages.length > 1 && (
					<div className="flex justify-between items-center mt-3">
						<button
							onClick={() => onNavigateImage(currentImageIndex - 1)}
							disabled={currentImageIndex === 0}
							className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
						>
							<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
							</svg>
						</button>
						<button
							onClick={() => onNavigateImage(currentImageIndex + 1)}
							disabled={currentImageIndex === generatedImages.length - 1}
							className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
						>
							<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
							</svg>
						</button>
					</div>
				)}

				{/* Action Buttons */}
				<div className="flex space-x-3 pt-3 border-t border-gray-200 mt-3">
					<button
						onClick={onGenerateNew}
						className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
					>
						Generate New
					</button>
					<button
						onClick={() => onUseImage(imageUrl)}
						className="flex-1 px-4 py-2 text-sm font-medium bg-lightPurpleTheme hover:bg-addindigo text-white rounded-md transition-colors"
					>
						Use This Cover
					</button>
				</div>
			</div>
		</Modal>
	);
};

// --- MAIN COMPONENT ---
export const ColorPalettes = ({ onClose, onPaletteSelect, activeCategory, categoriesList }: ColorPaletteProps) => {
	const [selectedColors, setSelectedColors] = useState<string[] | null>(null);
	const [currentStep, setCurrentStep] = useState<'colors' | 'form'>('colors');
	const [generatedImages, setGeneratedImages] = useState<Array<{ url: string; mimeType?: string; timestamp: number }>>(
		[]
	);
	const [currentImageIndex, setCurrentImageIndex] = useState(0);
	const [isGeneratingImage, setIsGeneratingImage] = useState(false);
	const [showImageModal, setShowImageModal] = useState(false);
	const streamCancelRef = useRef<(() => void) | null>(null);
	const currentWorkspace = useSelector((state: any) => state.currentWorkspace);

	const handleSelect = (colors: string[]) => {
		setSelectedColors(colors);
		setCurrentStep('form');
	};

	const handleBackToColors = () => {
		setCurrentStep('colors');
		setSelectedColors(null);
		setGeneratedImages([]);
		setCurrentImageIndex(0);
		setShowImageModal(false);
	};

	const handleGenerateImage = async (formData: any) => {
		setIsGeneratingImage(true);
		try {
			// First generate the prompt
			const responsePrompt: any = await ai_services.generatePrompt(formData);
			console.log('AI generated prompt:', responsePrompt);

			// Clean up any existing stream before starting a new one
			if (streamCancelRef.current) {
				streamCancelRef.current();
				streamCancelRef.current = null;
			}

			// Start streamed image generation (uses runId + workspaceId)
			const { cancel } = await ai_services.generateImage({
				prompt: responsePrompt,
				category: formData?.occasion || activeCategory?.name || 'general',
				size: '1024x1024',
				quality: 'auto',
				workspaceId: currentWorkspace?._id,
				onEvent: (payload: any) => {
					const status = (payload as any)?.status as string | undefined;
					console.log('Cover generator stream event:', status, payload);

					if (status === 'completed') {
						const mediaItems = Array.isArray((payload as any)?.media) ? ((payload as any).media as any[]) : [];
						if (mediaItems.length) {
							const additions = mediaItems
								.filter((m) => m && typeof m.url === 'string')
								.map((m) => ({
									url: m.url as string,
									mimeType: m.mimeType as string | undefined,
									timestamp: Date.now(),
								}));
							const hadNone = generatedImages.length === 0;
							setGeneratedImages((prev) => [...prev, ...additions]);
							if (hadNone && additions.length > 0) setCurrentImageIndex(0);
							setShowImageModal(true);
						}
						setIsGeneratingImage(false);
						if (streamCancelRef.current) {
							streamCancelRef.current();
							streamCancelRef.current = null;
						}
					}

					if (status === 'insufficient_credits' || status === 'failed') {
						console.warn('Image generation halted:', payload);
						setIsGeneratingImage(false);
						if (streamCancelRef.current) {
							streamCancelRef.current();
							streamCancelRef.current = null;
						}
					}
				},
				onError: (err: unknown) => {
					console.error('Cover generator stream error:', err);
					setIsGeneratingImage(false);
					if (streamCancelRef.current) {
						streamCancelRef.current();
						streamCancelRef.current = null;
					}
				},
			});

			streamCancelRef.current = cancel;
		} catch (error) {
			console.error('Image generation failed:', error);
			setIsGeneratingImage(false);
		}
	};

	const handleUseImage = (imageUrl: string) => {
		// Convert data URL to file and handle like uploaded image
		onPaletteSelect(imageUrl);
		setShowImageModal(false);
		onClose();
	};

	const handleGenerateNew = () => {
		setShowImageModal(false);
		// Keep generated images, just close modal to generate new one
	};

	const handleNavigateImage = (index: number) => {
		if (index >= 0 && index < generatedImages.length) {
			setCurrentImageIndex(index);
		}
	};

	const handleCloseImageModal = () => {
		setShowImageModal(false);
		// Don't reset generated images, just close the modal
	};

	// Render based on current step
	if (currentStep === 'form' && selectedColors) {
		return (
			<>
				<div className="bg-white p-3 rounded-lg shadow-xl w-72 max-w-sm max-h-96 overflow-y-auto">
					<AIInputForm
						selectedColors={selectedColors}
						activeCategory={activeCategory}
						onBack={handleBackToColors}
						onGenerateImage={handleGenerateImage}
						onClose={onClose}
						categoriesList={categoriesList}
						isGenerating={isGeneratingImage}
					/>
				</div>
				{generatedImages.length > 0 && (
					<ImagePreviewModal
						open={showImageModal}
						onClose={handleCloseImageModal}
						generatedImages={generatedImages}
						currentImageIndex={currentImageIndex}
						selectedColors={selectedColors}
						onUseImage={handleUseImage}
						onGenerateNew={handleGenerateNew}
						onNavigateImage={handleNavigateImage}
					/>
				)}
			</>
		);
	}

	// Default: Color palette selection
	return (
		<div className="bg-gray-50 p-3 rounded-lg shadow-xl w-64 max-w-sm max-h-96">
			<div className="border-b border-gray-200 pb-2 mb-2">
				<div className="flex items-center justify-between">
					<div>
						<h2 className="text-sm font-medium text-gray-800 mb-1">Choose Color Palette</h2>
						<p className="text-xs text-gray-600">Select colors for your AI cover</p>
					</div>
					<button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
						<XMarkIcon />
					</button>
				</div>
			</div>

			<div className="overflow-y-auto pr-2 -mr-2" style={{ maxHeight: '300px' }}>
				{paletteLibrary.map((category) => (
					<PaletteCategory
						key={category.name}
						name={category.name}
						variations={category.variations}
						onSelect={handleSelect}
						selectedColors={selectedColors}
					/>
				))}
			</div>
		</div>
	);
};
